import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Paged carousel: shows `pageSize` items at a time, auto-advances one page every
// `interval` ms, pauses while the pointer is over the strip, and exposes prev/next
// arrows that move immediately (and restart the auto timer). Wraps around.
export default function PagedCarousel({ children, pageSize = 6, interval = 2000 }) {
    const items = React.Children.toArray(children);
    const pageCount = Math.ceil(items.length / pageSize);
    const [page, setPage] = useState(0);
    const [paused, setPaused] = useState(false);
    const pausedRef = useRef(paused);
    pausedRef.current = paused;

    // Keep page in range if the item list shrinks.
    useEffect(() => {
        if (page > pageCount - 1) setPage(0);
    }, [pageCount, page]);

    // Auto-advance. Re-created whenever page/pauses/pageCount change so a manual
    // arrow click (which changes `page`) also restarts the 2s timer cleanly.
    useEffect(() => {
        if (pageCount <= 1 || paused) return undefined;
        const id = setInterval(() => {
            if (!pausedRef.current) {
                setPage(p => (p + 1) % pageCount);
            }
        }, interval);
        return () => clearInterval(id);
    }, [page, paused, pageCount, interval]);

    if (items.length === 0) return null;

    const goPrev = () => setPage(p => (p - 1 + pageCount) % pageCount);
    const goNext = () => setPage(p => (p + 1) % pageCount);

    const start = page * pageSize;
    const visible = items.slice(start, start + pageSize);

    const arrowStyle = {
        background: '#ffffff',
        border: '1px solid #ede9fe',
        borderRadius: '9999px',
        width: 40,
        height: 40,
        minWidth: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#4c1d95',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    };

    return (
        <div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
                {pageCount > 1 && (
                    <button type="button" onClick={goPrev} style={arrowStyle} aria-label="Previous">
                        <ChevronLeft size={22} />
                    </button>
                )}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', flexGrow: 1 }}>
                    {visible}
                </div>
                {pageCount > 1 && (
                    <button type="button" onClick={goNext} style={arrowStyle} aria-label="Next">
                        <ChevronRight size={22} />
                    </button>
                )}
            </div>

            {pageCount > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.4rem', marginTop: '0.75rem' }}>
                    {Array.from({ length: pageCount }, (_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setPage(i)}
                            aria-label={`Go to page ${i + 1}`}
                            style={{
                                width: 9,
                                height: 9,
                                borderRadius: '9999px',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                background: i === page ? '#7c3aed' : '#ddd6fe',
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
